import { useRouter } from 'next/router';
import { useState, useMemo, useCallback, useRef } from 'react';

import { useCompany } from 'context/company';
import { IEmployeeContract } from 'hooks/useEmployeeContractSWR';
import SearchDropdown from './searchDropdown';
import { useUser } from 'context/user';
import {
  AdminRoutes,
  AdminRoutesWithAccess,
  ContractorRoutes,
  IRouteInfoAdmin,
  IRouteInfoWorker,
  WorkerRoutesWithAccess,
} from 'routes';
import { EmployeeType } from 'lib/constants';
import { IHelperLabels, IEmployeeData } from './type';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import useDebounce from 'hooks/useDebounce';

export type EmployeeDataWithType = IEmployeeData & { employmentType: EmployeeType };

const SearchBar = ({ onOpenCommandPalette }: { onOpenCommandPalette?: () => void }) => {
  const router = useRouter();
  const { company } = useCompany();
  const { subRoleType } = useUser();
  const { affiliationsByCompany, roleType } = useUser();
  const { isAllowed } = useRouteAuthorization();
  const isWorker = roleType === 'worker';
  const isManager = roleType === 'manager';
  const placeholderText = `Search for anything...`; // isWorker ? 'Search your contracts' : 'Search for people or contracts';

  const employeeContracts = isWorker
    ? (Object.values(affiliationsByCompany || {}).flatMap((item) =>
        item.contracts.map((contract) => ({
          company: item.company,
          contract: contract,
        })),
      ) as unknown as IEmployeeContract[])
    : undefined;
  const employeeData = isManager ? company?.employeesAndEmployment : undefined;
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredActionData, setFilteredActionData] = useState<IHelperLabels[]>([]);
  const [filteredPeopleData, setFilteredPeopleData] = useState<EmployeeDataWithType[]>([]);
  const [filteredContractData, setFilteredContractData] = useState<IEmployeeContract[]>([]);
  const latestInputValueRef = useRef<string>('');

  // NOTE: Rendering large result sets (e.g. 1500+ employees/contracts) can freeze the UI.
  // We cap results to keep typing responsive and encourage users to narrow the query.
  const MAX_RESULTS_PER_SECTION = 100;

  const helperLabels = useMemo(() => {
    const helperLabelTexts: IHelperLabels[] = [];
    const routesWithHelperLabel = isWorker ? WorkerRoutesWithAccess : AdminRoutesWithAccess;

    routesWithHelperLabel &&
      Object.values(routesWithHelperLabel).map((routeInfo: IRouteInfoAdmin | IRouteInfoWorker) => {
        if (routeInfo?.helperLabel && isAllowed(routeInfo?.access)) {
          helperLabelTexts.push({
            helperLabel: routeInfo.helperLabel,
            href: routeInfo.route,
          });
        }
      });

    return helperLabelTexts;
  }, [isWorker, isAllowed]);

  /**
   * This function is called when the user types in the search bar
   */
  const runFiltering = useCallback(() => {
    const query = latestInputValueRef.current.trim().toLowerCase();

    if (query.length === 0) {
      setFilteredActionData([]);
      setFilteredPeopleData([]);
      setFilteredContractData([]);
      return;
    }

    // Filter action data (routes)
    setFilteredActionData(
      helperLabels.filter((item) => item.helperLabel.toLowerCase().includes(query)).slice(0, MAX_RESULTS_PER_SECTION),
    );

    if (employeeData) {
      const filteredEmployees: EmployeeDataWithType[] = [];
      const processedUserIds = new Set<string>();

      // Use a plain for..of loop to allow early exit once we reach MAX_RESULTS_PER_SECTION.
      for (const employee of employeeData) {
        if (filteredEmployees.length >= MAX_RESULTS_PER_SECTION) break;

        const matches =
          `${employee.user.firstName} ${employee.user.lastName}`.toLowerCase().includes(query) ||
          employee.user.email.toLowerCase().includes(query);

        if (!matches) continue;

        const userId = employee.user.userID;
        if (processedUserIds.has(userId)) continue;
        processedUserIds.add(userId);

        const employmentTypes = new Set<EmployeeType | 'US_EMPLOYEE'>();
        for (const employment of employee.employments) {
          if (filteredEmployees.length >= MAX_RESULTS_PER_SECTION) break;

          if (employment.type === EmployeeType.CONTRACTOR && !employmentTypes.has(EmployeeType.CONTRACTOR)) {
            employmentTypes.add(EmployeeType.CONTRACTOR);
            filteredEmployees.push({ ...employee, employmentType: EmployeeType.CONTRACTOR });
          }
          if (employment.type === EmployeeType.EMPLOYEE && !employmentTypes.has(EmployeeType.EMPLOYEE)) {
            employmentTypes.add(EmployeeType.EMPLOYEE);
            filteredEmployees.push({ ...employee, employmentType: EmployeeType.EMPLOYEE });
          }
          if (employment.w2Payroll?.checkEmployeeId && !employmentTypes.has(EmployeeType.US_EMPLOYEE)) {
            employmentTypes.add(EmployeeType.US_EMPLOYEE);
            filteredEmployees.push({ ...employee, employmentType: EmployeeType.US_EMPLOYEE });
          }
        }
      }

      setFilteredPeopleData(filteredEmployees);
    } else {
      setFilteredPeopleData([]);
    }

    if (employeeContracts) {
      setFilteredContractData(
        employeeContracts
          .filter(
            (item: IEmployeeContract) =>
              subRoleType === 'contractor' &&
              item.contract.type === EmployeeType.CONTRACTOR &&
              (item.contract?.name?.toLowerCase().includes(query) ||
                item.company.companyName.toLowerCase().includes(query)),
          )
          .slice(0, MAX_RESULTS_PER_SECTION),
      );
    } else {
      setFilteredContractData([]);
    }
  }, [employeeContracts, employeeData, helperLabels, subRoleType]);

  // Debounce filtering so typing doesn't block the main thread on large datasets.
  const debouncedRunFiltering = useDebounce(runFiltering);

  const handleInputChange = (textInputData: string) => {
    latestInputValueRef.current = textInputData ?? '';
    setInputValue(textInputData);

    if ((textInputData ?? '').trim().length === 0) {
      // Cancel any pending work and clear immediately.
      // Lodash debounced functions have cancel(); we keep it optional to avoid typing issues.
      (debouncedRunFiltering as any)?.cancel?.();
      setFilteredActionData([]);
      setFilteredPeopleData([]);
      setFilteredContractData([]);
      return;
    }

    debouncedRunFiltering();
  };

  /**
   * This function is called when the user selects an item from the dropdown
   */
  const handleItemChange = (item: EmployeeDataWithType | IEmployeeContract | null) => {
    if (item === null) {
      setInputValue('');
      setFilteredActionData([]);
      setFilteredPeopleData([]);
      setFilteredContractData([]);
      return;
    }
    if ('contract' in item) {
      // It's a contract item
      const contractName = item.contract?.name || `Contract for ${item.company.companyName}`;
      setInputValue(contractName);
      setTimeout(() => {
        onEnterPress(contractName);
      }, 0);
    } else {
      // It's an employee item
      const selectedEmail = item.user.email;
      setInputValue(selectedEmail);
      setTimeout(() => {
        onEnterPress(selectedEmail, item.employmentType);
      }, 0);
    }
  };

  const onEnterPress = useCallback(
    (selectedEmail?: string, selectedEmploymentType?: EmployeeType) => {
      // Defensive: this callback is typed as optional; avoid runtime crashes if called without a value.
      if (!selectedEmail) return;

      let targetHref = '';
      const inputVal = selectedEmail.toLowerCase();

      // First, check if it's an action item
      const actionItem = helperLabels?.find((item) => item.helperLabel.toLowerCase().trim().includes(inputVal));

      if (actionItem) {
        targetHref = actionItem.href;
      } else {
        // IMPORTANT: Do not rely on the filtered lists here (they are debounced).
        // We search the source employee data so "Enter" works reliably even when filtering hasn't completed yet.
        const selectedPerson =
          employeeData
            ?.flatMap((employee) => {
              const matches = employee.user.email.toLowerCase() === inputVal;
              if (!matches) return [];

              const results: EmployeeDataWithType[] = [];
              const types = new Set<EmployeeType | 'US_EMPLOYEE'>();

              for (const employment of employee.employments) {
                if (employment.type === EmployeeType.CONTRACTOR && !types.has(EmployeeType.CONTRACTOR)) {
                  types.add(EmployeeType.CONTRACTOR);
                  results.push({ ...employee, employmentType: EmployeeType.CONTRACTOR });
                }
                if (employment.type === EmployeeType.EMPLOYEE && !types.has(EmployeeType.EMPLOYEE)) {
                  types.add(EmployeeType.EMPLOYEE);
                  results.push({ ...employee, employmentType: EmployeeType.EMPLOYEE });
                }
                if (employment.w2Payroll?.checkEmployeeId && !types.has(EmployeeType.US_EMPLOYEE)) {
                  types.add(EmployeeType.US_EMPLOYEE);
                  results.push({ ...employee, employmentType: EmployeeType.US_EMPLOYEE });
                }
              }

              return results;
            })
            .find((item) => item.employmentType === selectedEmploymentType) ?? null;

        if (selectedPerson) {
          if (selectedPerson.employmentType === EmployeeType.CONTRACTOR) {
            targetHref = `${AdminRoutes.ContractorProfile}/${selectedPerson.user.userID}`;
          } else if (selectedPerson.employmentType === EmployeeType.EMPLOYEE) {
            targetHref = `${AdminRoutes.EOREmployeeProfile}/${selectedPerson.user.userID}`;
          } else if (selectedPerson.employmentType === EmployeeType.US_EMPLOYEE) {
            const w2Employment = selectedPerson.employments.find((emp) => emp.w2Payroll?.checkEmployeeId);
            if (w2Employment?.w2Payroll?.checkEmployeeId) {
              targetHref = `${AdminRoutes.EmployeeProfile}/${w2Employment.w2Payroll.checkEmployeeId}`;
            }
          }
        } else {
          // If no matching person, check for a contract match
          const contractItem = employeeContracts?.find(
            (item) =>
              item.contract.type === EmployeeType.CONTRACTOR &&
              (item.contract?.name?.toLowerCase().trim().includes(inputVal) ||
                item.company.companyName.toLowerCase().trim().includes(inputVal)),
          );
          const checkCompanyID = contractItem?.company?.w2Payroll?.checkId;

          if (contractItem) {
            targetHref = ContractorRoutes?.ContractDetails.replace('[contractID]', contractItem?.contract.id);
          } else if (checkCompanyID) {
            targetHref = ContractorRoutes?.EmployerProfile.replace('[companyID]', checkCompanyID);
          }
        }
      }

      // Perform navigation if a targetHref is found
      if (targetHref) {
        // Clear the filtered data
        setFilteredActionData([]);
        setFilteredPeopleData([]);
        setFilteredContractData([]);

        // Reset the input value
        setTimeout(() => {
          setInputValue('');
        }, 0);

        router.push(targetHref);
      }
    },
    [employeeContracts, employeeData, helperLabels, router],
  );

  return (
    <SearchDropdown
      name="SearchDropdown"
      selectedItem={inputValue}
      handleInputChange={handleInputChange}
      onSelectedItemChange={handleItemChange}
      filteredActionValues={filteredActionData}
      filteredPeopleValues={filteredPeopleData}
      filteredContractValues={filteredContractData}
      onEnterPress={onEnterPress}
      placeholderText={placeholderText}
      isNavbar={true}
      onOpenCommandPalette={onOpenCommandPalette}
    />
  );
};

export default SearchBar;
